import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { AuthActionTypes, getUser, useAuth } from '@devd-client/devd/auth';
import {
  DateFilter,
  ISelect,
  TeamMenu,
  durationData,
  PeriodType,
  useDashboardStore,
} from '@devd-client/devd/components';
import { Select } from 'chakra-react-select';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useWorkflowStageTimeGql } from '../../graphql';
import { getWorkflowStageContributorsGraphql } from '../../graphql/query';
import { WorkflowStageComponentTime } from '../../helpers/report.types';

const STAGES = ['Backlog', 'In Progress', 'In Review', 'Done', 'Archived'];

const HEADER_BG = '#FBFBFB';
const BORDER = '1px solid #E0E0E0';

function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return '—';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

type CellMap = Record<
  string,
  Record<string, { avgMinutes: number; issueCount: number }>
>;

function buildCellMap(rows: WorkflowStageComponentTime[]): CellMap {
  const map: CellMap = {};
  for (const row of rows) {
    if (!map[row.component]) map[row.component] = {};
    map[row.component][row.stage] = {
      avgMinutes: row.avgMinutes,
      issueCount: row.issueCount,
    };
  }
  return map;
}

function deriveComponents(rows: WorkflowStageComponentTime[]): string[] {
  const seen = new Set<string>();
  const order: string[] = [];
  for (const row of rows) {
    if (!seen.has(row.component)) {
      seen.add(row.component);
      order.push(row.component);
    }
  }
  return order.sort();
}

function heatColor(minutes: number | undefined, maxMinutes: number): string {
  if (!minutes || minutes <= 0 || maxMinutes === 0) return 'white';
  const ratio = Math.min(minutes / maxMinutes, 1);
  const lightness = Math.round(95 - ratio * 35);
  return `hsl(217, 70%, ${lightness}%)`;
}

const COMPONENT_COL_WIDTH = '300px';
const STAGE_COL_WIDTH = '180px';

export const WorkflowStageTimeTable = () => {
  const { selected, setSelected } = useDashboardStore();
  const [appState, dispatch] = useAuth();
  const [epicKeyInput, setEpicKeyInput] = useState('');
  const [appliedEpicKey, setAppliedEpicKey] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [selectedTeam, setSelectedTeam] = useState<ISelect>({
    value: appState?.user?.dto?.team || '',
    label: appState?.user?.dto?.team || 'Organisation',
  });

  useEffect(() => {
    if (selected.startDate && selected.endDate) return;
    const sprintPref = appState?.user?.dto?.sprintDetail;
    if (appState?.user?.dto?.sprint && sprintPref) {
      try {
        const parsed = JSON.parse(sprintPref);
        setSelected({
          ...(durationData[6] as PeriodType),
          startDate: moment(parsed?.startDate).format('YYYY-MM-DD'),
          endDate: moment(parsed?.endDate).format('YYYY-MM-DD'),
          sprintId: appState.user.dto.sprint,
          sprintName: parsed?.sprintName,
        });
        return;
      } catch {
        // fall through
      }
    }
    setSelected(durationData[3] as PeriodType);
  }, []);

  const teamId = useMemo(
    () =>
      selectedTeam.value && selectedTeam.label !== 'Organisation'
        ? selectedTeam.value
        : '',
    [selectedTeam]
  );

  const { data: contributorList } = useQuery(
    [
      'workflow-stage-contributors',
      selected.startDate,
      selected.endDate,
      teamId,
    ],
    () =>
      getWorkflowStageContributorsGraphql({
        startDate: selected.startDate,
        endDate: selected.endDate,
        teamId,
        sprintId: selected.sprintId || '',
      }),
    {
      enabled: !!selected.startDate && !!selected.endDate,
      select: (response) => response?.ContributorDetailsV2?.data ?? [],
    }
  );

  const contributorOptions = useMemo(
    () =>
      contributorList?.map((person: { name: string; email: string }) => ({
        value: person.email || person.name,
        label: person.name || person.email,
      })) ?? [],
    [contributorList]
  );

  useEffect(() => {
    setSelectedUser(null);
  }, [teamId]);

  const handleEpicKeyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') setAppliedEpicKey(epicKeyInput.trim());
    },
    [epicKeyInput]
  );

  const { data, isFetching } = useWorkflowStageTimeGql({
    startDate: selected.startDate,
    endDate: selected.endDate,
    teamId,
    sprintId: selected.sprintId || '',
    epicKey: appliedEpicKey || undefined,
    author: selectedUser?.value || undefined,
  });

  const rows: WorkflowStageComponentTime[] = useMemo(() => {
    const items = data?.data;
    return Array.isArray(items) ? items : [];
  }, [data?.data]);

  const components = useMemo(() => deriveComponents(rows), [rows]);
  const cellMap = useMemo(() => buildCellMap(rows), [rows]);

  const maxMinutes = useMemo(() => {
    let max = 0;
    for (const row of rows) {
      if ((row.avgMinutes ?? 0) > max) max = row.avgMinutes;
    }
    return max;
  }, [rows]);

  const gridTemplateColumns = useMemo(
    () =>
      `${COMPONENT_COL_WIDTH} ${STAGES.map(() => STAGE_COL_WIDTH).join(' ')}`,
    []
  );

  const skeletonRows = 5;

  return (
    <Flex direction="column" w="100%">
      <Flex align="center" justify="space-between">
        <Heading as="h1" size="md">
          Workflow Stage Time by Component
        </Heading>
        <DateFilter
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
          AuthActionTypes={AuthActionTypes}
        />
      </Flex>

      <Flex align="flex-end" gap={3} mt={4} flexWrap="wrap">
        <TeamMenu
          selectedTeam={selectedTeam}
          handleSelectedTeam={setSelectedTeam}
          AuthActionTypes={AuthActionTypes}
          getUser={getUser}
          appState={appState}
          dispatch={dispatch}
          hideShowDetails
        />
        <Box minW="200px">
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="semibold"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            Epic Key
          </Text>
          <InputGroup size="sm">
            <Input
              placeholder="e.g. PROJ-42 (press Enter)"
              value={epicKeyInput}
              onChange={(e) => setEpicKeyInput(e.target.value)}
              onKeyDown={handleEpicKeyKeyDown}
              borderRadius="md"
              bg="white"
              fontSize="sm"
              fontFamily="heading"
            />
            {appliedEpicKey && (
              <InputRightElement width="auto" pr={2}>
                <Text
                  fontSize="xs"
                  color="blue.500"
                  fontFamily="heading"
                  cursor="pointer"
                  onClick={() => {
                    setEpicKeyInput('');
                    setAppliedEpicKey('');
                  }}
                >
                  ✕
                </Text>
              </InputRightElement>
            )}
          </InputGroup>
        </Box>
        <Box minW="220px" maxW="320px">
          <Text
            fontSize="sm"
            fontFamily="heading"
            fontWeight="semibold"
            color="text.secondary"
            lineHeight="17px"
            mb={2}
          >
            User
          </Text>
          <Select
            placeholder="All users"
            isClearable
            useBasicStyles
            size="sm"
            value={selectedUser}
            onChange={(val: { value: string; label: string } | null) =>
              setSelectedUser(val)
            }
            options={contributorOptions}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              menuPortal: (base: object) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </Box>
      </Flex>

      {(appliedEpicKey || selectedUser) && (
        <Text mt={2} fontSize="xs" fontFamily="heading" color="text.secondary">
          {appliedEpicKey && (
            <>
              Epic: <strong>{appliedEpicKey}</strong>
            </>
          )}
          {appliedEpicKey && selectedUser && ' · '}
          {selectedUser && (
            <>
              User: <strong>{selectedUser.label}</strong>
            </>
          )}
        </Text>
      )}

      <Box
        mt={3}
        overflowX="auto"
        overflowY="auto"
        border={BORDER}
        height="66vh"
        width="100%"
        borderRadius="8px"
        position="relative"
        bg="white"
      >
        <Grid templateColumns={gridTemplateColumns} w="max-content" minW="100%">
          {/* Header row */}
          <GridItem
            position="sticky"
            top={0}
            left={0}
            zIndex={21}
            bg={HEADER_BG}
            minH="52px"
            display="flex"
            alignItems="center"
            px={4}
            py={2}
            borderBottom={BORDER}
            boxShadow="1px 0 0 0 #E0E0E0"
          >
            <Text
              fontSize="sm"
              fontFamily="heading"
              fontWeight="medium"
              color="text.secondary2"
            >
              Component
            </Text>
          </GridItem>

          {STAGES.map((stage) => (
            <GridItem
              key={`h-${stage}`}
              position="sticky"
              top={0}
              zIndex={20}
              bg={HEADER_BG}
              minH="52px"
              display="flex"
              alignItems="center"
              px={4}
              py={2}
              borderBottom={BORDER}
              borderLeft={BORDER}
            >
              <Text
                fontSize="sm"
                fontFamily="heading"
                fontWeight="medium"
                color="text.secondary2"
                noOfLines={1}
              >
                {stage}
              </Text>
            </GridItem>
          ))}

          {/* Data rows */}
          {isFetching ? (
            [...Array(skeletonRows)].map((_, rowIdx) => (
              <>
                <GridItem
                  key={`sk-comp-${rowIdx}`}
                  position="sticky"
                  left={0}
                  zIndex={10}
                  bg="white"
                  minH="52px"
                  display="flex"
                  alignItems="center"
                  px={4}
                  py={3}
                  borderBottom={BORDER}
                  boxShadow="1px 0 0 0 #E0E0E0"
                >
                  <Skeleton height="14px" width="140px" />
                </GridItem>
                {STAGES.map((stage) => (
                  <GridItem
                    key={`sk-${rowIdx}-${stage}`}
                    bg="white"
                    minH="52px"
                    display="flex"
                    alignItems="center"
                    px={4}
                    py={3}
                    borderBottom={BORDER}
                    borderLeft={BORDER}
                  >
                    <Skeleton height="14px" width="60px" />
                  </GridItem>
                ))}
              </>
            ))
          ) : components.length === 0 ? (
            <GridItem
              colSpan={STAGES.length + 1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              minH="200px"
            >
              <Text fontSize="sm" fontFamily="heading" color="text.secondary">
                No data found for the selected filters.
              </Text>
            </GridItem>
          ) : (
            components.map((component, rowIdx) => (
              <>
                <GridItem
                  key={`comp-${rowIdx}`}
                  position="sticky"
                  left={0}
                  zIndex={10}
                  bg="white"
                  minH="52px"
                  display="flex"
                  alignItems="center"
                  px={4}
                  py={3}
                  borderBottom={BORDER}
                  boxShadow="1px 0 0 0 #E0E0E0"
                >
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    color="text.primary"
                    fontWeight="medium"
                    noOfLines={1}
                  >
                    {component}
                  </Text>
                </GridItem>

                {STAGES.map((stage) => {
                  const cell = cellMap[component]?.[stage];
                  const bg = heatColor(cell?.avgMinutes, maxMinutes);
                  return (
                    <Tooltip
                      key={`cell-${rowIdx}-${stage}`}
                      label={
                        cell
                          ? `Avg: ${formatMinutes(cell.avgMinutes)} · ${
                              cell.issueCount
                            } issue${cell.issueCount !== 1 ? 's' : ''}`
                          : 'No data'
                      }
                      hasArrow
                      placement="top"
                    >
                      <GridItem
                        bg={bg}
                        minH="52px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        px={4}
                        py={3}
                        borderBottom={BORDER}
                        borderLeft={BORDER}
                        cursor={cell ? 'default' : undefined}
                      >
                        <Text
                          fontSize="sm"
                          fontFamily="heading"
                          color={
                            bg === 'white' ? 'text.secondary' : 'text.primary'
                          }
                          fontWeight={cell ? 'medium' : 'normal'}
                          noOfLines={1}
                        >
                          {formatMinutes(cell?.avgMinutes)}
                        </Text>
                      </GridItem>
                    </Tooltip>
                  );
                })}
              </>
            ))
          )}
        </Grid>
      </Box>

      <Text mt={2} fontSize="xs" fontFamily="heading" color="text.secondary">
        Cell shows average time spent in each workflow stage. Hover for issue
        count. Darker blue = longer average time. User filter includes tickets
        that were ever assigned to the selected person. Press Enter after typing
        an epic key to filter.
      </Text>
    </Flex>
  );
};

export default WorkflowStageTimeTable;
